
import {
  getThreadRetention,
  updateThreadRetention,
} from "../api/settings.js";

const styles = css`
  .retention-row {
    align-items: center;
    display: flex;
    gap: 16px;
    justify-content: space-between;
  }

  .retention-info {
    min-width: 0;
  }

  .name {
    color: var(--foreground);
    font-size: 14px;
    font-weight: 600;
  }

  .desc {
    color: var(--muted-foreground);
    font-size: 12px;
    line-height: 1.45;
    margin-top: 3px;
  }

  .retention-days {
    align-items: center;
    color: var(--muted-foreground);
    display: flex;
    flex-wrap: wrap;
    font-size: 14px;
    gap: 8px;
    margin-top: 4px;
  }

  .retention-days input {
    background: var(--background);
    border: 1px solid var(--input);
    border-radius: 8px;
    box-sizing: border-box;
    color: var(--foreground);
    font: inherit;
    font-size: 14px;
    height: 34px;
    outline: none;
    padding: 8px 10px;
    text-align: right;
    width: 76px;
  }

  .retention-days input:focus {
    border-color: var(--ring);
    box-shadow: 0 0 0 3px var(--ring-shadow);
  }

  .retention-days.off {
    opacity: 0.5;
  }

  .status-row {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
  }

  .actions app-button {
    width: auto;
  }

  .saved {
    color: var(--muted-foreground);
    font-size: 13px;
  }

  .error {
    color: var(--destructive);
    font-size: 13px;
    margin: 8px 0 0;
  }

  .error:empty {
    display: none;
  }
`;

export class AppSettingsThreads extends HTMLElement {
  static argonHydrate(element) {
    if (!(element instanceof AppSettingsThreads)) return;
    if (element.isConnected && !element.#bindingsMounted) {
      element.#syncHydratedProps();
      element.connectedCallback();
    }
    if (element.#root) element.#wireEvents(element.#root);
  }
  static observedAttributes = [];

  #archiveEnabled;
  #archiveDays;
  #removeEnabled;
  #removeDays;
  #loaded;
  #error;
  #saved;
  
  #textStart0;
  #textEnd0;
  #textLast0;
  #textStart1;
  #textEnd1;
  #textLast1;
  #attr0;
  #attr1;
  #attr2;
  #attr3;
  #attr4;
  #attr5;
  #attrLast0;
  #attrLast1;
  #attrLast2;
  #attrLast3;
  #attrLast4;
  #attrLast5;
  #hooksLive = false;
  #cleanups = [];
  #root;
  #bindingsMounted = false;
  #hydrating = false;
  #isCSR = false;

  #setArchiveEnabled(value) {
    if (Object.is(this.#archiveEnabled, value)) return;
    this.#archiveEnabled = value;
    this.#updateBindings('archiveEnabled');
  }

  #setArchiveDays(value) {
    if (Object.is(this.#archiveDays, value)) return;
    this.#archiveDays = value;
    this.#updateBindings('archiveDays');
  }

  #setRemoveEnabled(value) {
    if (Object.is(this.#removeEnabled, value)) return;
    this.#removeEnabled = value;
    this.#updateBindings('removeEnabled');
  }

  #setRemoveDays(value) {
    if (Object.is(this.#removeDays, value)) return;
    this.#removeDays = value;
    this.#updateBindings('removeDays');
  }

  #setLoaded(value) {
    if (Object.is(this.#loaded, value)) return;
    this.#loaded = value;
    this.#updateBindings('loaded');
  }

  #setError(value) {
    if (Object.is(this.#error, value)) return;
    this.#error = value;
    this.#updateBindings('error');
  }

  #setSaved(value) {
    if (Object.is(this.#saved, value)) return;
    this.#saved = value;
    this.#updateBindings('saved');
  }

  #syncHydratedProps() {
    if (this.#isCSR) return;

  }

  constructor(props) {
    super();
    if (props !== undefined) {
      this.#isCSR = true;

      this.#initState();
      const shadow = this.attachShadow({ mode: 'open' });
      shadow.innerHTML = this.#render();
      this.#mount(shadow, false);
    }
  }

  connectedCallback() {
    if (!this.#isCSR && !this.#bindingsMounted) {
      this.#syncHydratedProps();
    this.#initState();
    const shadow = this.shadowRoot;
    const root = shadow ?? this.attachShadow({ mode: 'open' });
    if (!shadow) root.innerHTML = this.#render();
    this.#mount(root, !!shadow);
    }
    if (this.#hooksLive) return;
    this.#hooksLive = true;
    {
      const cleanup = (() => {
    void getThreadRetention()
        .then((settings) => {
        this.#setArchiveEnabled(settings.archive_after_days != null);
        this.#setArchiveDays(settings.archive_after_days ?? 14);
        this.#setRemoveEnabled(settings.remove_after_days != null);
        this.#setRemoveDays(settings.remove_after_days ?? 90);
        this.#setLoaded(true);
        this.#setError("");
    })
        .catch(() => {
        this.#setError("Failed to load thread settings.");
    });
})();
      if (typeof cleanup === 'function') this.#cleanups.push(cleanup);
    }
  }

  disconnectedCallback() {
    this.#hooksLive = false;
    for (const cleanup of this.#cleanups.splice(0)) cleanup();
  }


  #initState() {
    this.#archiveEnabled = true;
    this.#archiveDays = 14;
    this.#removeEnabled = true;
    this.#removeDays = 90;
    this.#loaded = false;
    this.#error = "";
    this.#saved = false;
  }

  #render() {
    
    return `<style>${this.#escText(styles)}</style><app-settings-section title="Auto-archive" description="Archive threads automatically once they have been inactive for a while. Archived threads leave the sidebar but keep all messages and files, and can be restored anytime."><div class="retention-row"><div class="retention-info"><div class="name">Archive inactive threads</div><div class="desc">Turn this off to keep every thread in the sidebar until you archive it yourself.</div></div><app-switch${this.#archiveEnabled ? ' checked' : ''} data-argon="2936" data-argon-bind="0"></app-switch></div><div class="${this.#escAttr(this.#archiveEnabled ? "retention-days" : "retention-days off")}" data-argon-bind="1"><span>Archive after</span><input name="archive-days" type="number" min="1" max="3650" value="${String(this.#archiveDays)}" autocomplete="off" data-argon="3542" data-argon-bind="2"><span>days of inactivity</span></div></app-settings-section><app-settings-section title="Auto-remove" description="Permanently delete an archived thread — including its workspace files and version history — once it has stayed archived long enough. This cannot be undone."><div class="retention-row"><div class="retention-info"><div class="name">Delete old archived threads</div><div class="desc">Turn this off to keep archived threads until you delete them yourself.</div></div><app-switch${this.#removeEnabled ? ' checked' : ''} data-argon="4371" data-argon-bind="3"></app-switch></div><div class="${this.#escAttr(this.#removeEnabled ? "retention-days" : "retention-days off")}" data-argon-bind="4"><span>Delete after</span><input name="remove-days" type="number" min="1" max="3650" value="${String(this.#removeDays)}" autocomplete="off" data-argon="4972" data-argon-bind="5"><span>days since archival</span></div></app-settings-section><div class="status-row actions"><app-button data-argon="5293">Save changes</app-button><!--argon-text:0-->${this.#saved ? "<span class=\"saved\">Saved.</span>" : this.#escText("")}${this.#loaded ? this.#escText("") : "<span class=\"saved\">Loading...</span>"}<!--/argon-text:0--></div><p class="error"><!--argon-text:1-->${this.#escText(this.#error)}<!--/argon-text:1--></p>`;
  }



  #mount(root, hydrating) {
    this.#root = root;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_COMMENT);
    let marker;
    while ((marker = walker.nextNode())) {
      switch (marker.nodeValue) {
        case 'argon-text:0':
          this.#textStart0 = marker;
          break;
        case '/argon-text:0':
          this.#textEnd0 = marker;
          break;
        case 'argon-text:1':
          this.#textStart1 = marker;
          break;
        case '/argon-text:1':
          this.#textEnd1 = marker;
          break;
      }
    }
    this.#attr0 = root.querySelector('[data-argon-bind="0"]');
    this.#attr1 = root.querySelector('[data-argon-bind="1"]');
    this.#attr2 = root.querySelector('[data-argon-bind="2"]');
    this.#attr3 = root.querySelector('[data-argon-bind="3"]');
    this.#attr4 = root.querySelector('[data-argon-bind="4"]');
    this.#attr5 = root.querySelector('[data-argon-bind="5"]');
    this.#bindingsMounted = true;
    this.#hydrating = hydrating;
    this.#updateBindings();
    this.#hydrating = false;
    this.#wireEvents(root);
    if (!root.__argonEventObserver) {
      const observer = new window.MutationObserver(() => this.#wireEvents(root));
      observer.observe(root, { childList: true, subtree: true });
      Object.defineProperty(root, '__argonEventObserver', { value: observer });
    }
    this.setAttribute('hydrated', '');
    setTimeout(() => { if (this.#root === root) this.#wireEvents(root); }, 0);
  }

  #replaceRange(start, end, value) {
    if (!start || !end) return;
    const html = String(value);
    const sole = start.nextSibling;
    if (sole && sole !== end && sole.nextSibling === end && sole.nodeType === 3 && !/[<&]/.test(html)) {
      if (html.startsWith(sole.data)) sole.appendData(html.slice(sole.data.length));
      else sole.data = html;
      return;
    }
    const template = document.createElement('template');
    template.innerHTML = html;
    if (sole && sole !== end && sole.nextSibling === end && template.content.childNodes.length === 1 && this.#patchElement(sole, template.content.firstChild)) return;
    let oldNode = start.nextSibling;
    let newNode = template.content.firstChild;
    while (newNode) {
      const nextNew = newNode.nextSibling;
      if (!oldNode || oldNode === end) {
        end.before(newNode);
        if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
      } else {
        const nextOld = oldNode.nextSibling;
        if (!this.#patchNode(oldNode, newNode)) {
          oldNode.before(newNode);
          oldNode.remove();
          if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
        }
        oldNode = nextOld;
      }
      newNode = nextNew;
    }
    while (oldNode && oldNode !== end) {
      const nextOld = oldNode.nextSibling;
      oldNode.remove();
      oldNode = nextOld;
    }
  }

  #patchElement(oldEl, newEl) {
    if (!oldEl || !newEl || oldEl.nodeType !== 1 || newEl.nodeType !== 1 || oldEl.localName !== newEl.localName) return false;
    for (const attr of [...oldEl.attributes]) {
      if (attr.name !== 'hydrated' && !newEl.hasAttribute(attr.name)) oldEl.removeAttribute(attr.name);
    }
    for (const attr of [...newEl.attributes]) {
      if (oldEl.getAttribute(attr.name) !== attr.value) oldEl.setAttribute(attr.name, attr.value);
    }
    this.#patchChildren(oldEl, newEl);
    this.#wireEvents(oldEl);
    return true;
  }

  #patchNode(oldNode, newNode) {
    if (!oldNode || !newNode) return false;
    if (oldNode.nodeType === 3 && newNode.nodeType === 3) {
      if (oldNode.data !== newNode.data) oldNode.data = newNode.data;
      return true;
    }
    if (oldNode.nodeType === 8 && newNode.nodeType === 8) {
      if (oldNode.nodeValue !== newNode.nodeValue) oldNode.nodeValue = newNode.nodeValue;
      return true;
    }
    return this.#patchElement(oldNode, newNode);
  }

  #patchChildren(oldEl, newEl) {
    let oldNode = oldEl.firstChild;
    let newNode = newEl.firstChild;
    while (newNode) {
      const nextNew = newNode.nextSibling;
      if (!oldNode) {
        oldEl.append(newNode);
        if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
      } else {
        const nextOld = oldNode.nextSibling;
        if (!this.#patchNode(oldNode, newNode)) {
          oldEl.insertBefore(newNode, oldNode);
          oldNode.remove();
          if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
        }
        oldNode = nextOld;
      }
      newNode = nextNew;
    }
    while (oldNode) {
      const nextOld = oldNode.nextSibling;
      oldNode.remove();
      oldNode = nextOld;
    }
  }
  #escText(v) {
    return String(v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  #escAttr(v) {
    return String(v).replace(/&/g, '&amp;').replace(/"/g, '&quot;');
  }


  #eventTargets(scope, id) {
    const selector = `[data-argon="${id}"]`;
    const targets = [...scope.querySelectorAll(selector)];
    if (scope.nodeType === 1 && scope.matches(selector)) targets.unshift(scope);
    return targets;
  }

  #listen(el, id, event, handler) {
    const key = id + ':' + event;
    const root = this.#root;
    if (!root.__argonListeners) Object.defineProperty(root, '__argonListeners', { value: {} });
    const prev = root.__argonListeners[key];
    if (prev) root.removeEventListener(event, prev, true);
    const next = event => {
      const target = event.composedPath().find(node => node?.getAttribute?.('data-argon') === id);
      if (!target) return;
      const delegated = new Proxy(event, { get(source, prop) {
        if (prop === 'currentTarget') return target;
        const value = Reflect.get(source, prop, source);
        return typeof value === 'function' ? value.bind(source) : value;
      } });
      handler.call(target, delegated);
    };
    root.__argonListeners[key] = next;
    root.addEventListener(event, next, true);
  }

  #updateBindings(prop) {
    if (!this.#bindingsMounted) return;
    
    if (prop === undefined || prop === 'saved' || prop === 'loaded') {
      const __argonNext = `${this.#saved ? "<span class=\"saved\">Saved.</span>" : this.#escText("")}${this.#loaded ? this.#escText("") : "<span class=\"saved\">Loading...</span>"}`;
      if (__argonNext !== this.#textLast0) {
        this.#textLast0 = __argonNext;
        this.#replaceRange(this.#textStart0, this.#textEnd0, __argonNext);
      }
    }
    if (prop === undefined || prop === 'error') {
      const __argonNext = `${this.#escText(this.#error)}`;
      if (__argonNext !== this.#textLast1) {
        this.#textLast1 = __argonNext;
        this.#replaceRange(this.#textStart1, this.#textEnd1, __argonNext);
      }
    }
    if ((prop === undefined || prop === 'archiveEnabled') && this.#attr0) {
      this.#attr0.toggleAttribute('checked', !!(this.#archiveEnabled));
    }
    if ((prop === undefined || prop === 'archiveEnabled') && this.#attr1) {
      const __argonNext = `${this.#archiveEnabled ? "retention-days" : "retention-days off"}`;
      if (__argonNext !== this.#attrLast1) {
        this.#attrLast1 = __argonNext;
        this.#attr1.setAttribute('class', __argonNext);
      }
    }
    if ((prop === undefined || prop === 'archiveDays') && this.#attr2) {
      const __argonNext = `${String(this.#archiveDays)}`;
      if (__argonNext !== this.#attrLast2) {
        this.#attrLast2 = __argonNext;
        this.#attr2.setAttribute('value', __argonNext);
      }
    }
    if ((prop === undefined || prop === 'removeEnabled') && this.#attr3) {
      this.#attr3.toggleAttribute('checked', !!(this.#removeEnabled));
    }
    if ((prop === undefined || prop === 'removeEnabled') && this.#attr4) {
      const __argonNext = `${this.#removeEnabled ? "retention-days" : "retention-days off"}`;
      if (__argonNext !== this.#attrLast4) {
        this.#attrLast4 = __argonNext;
        this.#attr4.setAttribute('class', __argonNext);
      }
    }
    if ((prop === undefined || prop === 'removeDays') && this.#attr5) {
      const __argonNext = `${String(this.#removeDays)}`;
      if (__argonNext !== this.#attrLast5) {
        this.#attrLast5 = __argonNext;
        this.#attr5.setAttribute('value', __argonNext);
      }
    }
  }

  #wireEvents(scope) {
    const root = this.#root;
    this.#eventTargets(scope, '2936').forEach(el => this.#listen(el, '2936', 'change', (event: Event) => {
    const checked = (event as CustomEvent<{
        checked: boolean;
    }>).detail?.checked;
    if (typeof checked !== "boolean")
        return;
    this.#setArchiveEnabled(checked);
    this.#setSaved(false);
}));
    this.#eventTargets(scope, '3542').forEach(el => this.#listen(el, '3542', 'input', (event: Event) => {
    this.#setArchiveDays(Number((event.target as HTMLInputElement).value));
    this.#setSaved(false);
}));
    this.#eventTargets(scope, '4371').forEach(el => this.#listen(el, '4371', 'change', (event: Event) => {
    const checked = (event as CustomEvent<{
        checked: boolean;
    }>).detail?.checked;
    if (typeof checked !== "boolean")
        return;
    this.#setRemoveEnabled(checked);
    this.#setSaved(false);
}));
    this.#eventTargets(scope, '4972').forEach(el => this.#listen(el, '4972', 'input', (event: Event) => {
    this.#setRemoveDays(Number((event.target as HTMLInputElement).value));
    this.#setSaved(false);
}));
    this.#eventTargets(scope, '5293').forEach(el => this.#listen(el, '5293', 'click', () => {
    this.#setError("");
    this.#setSaved(false);
    const archive = Math.min(3650, Math.max(1, Math.round(this.#archiveDays) || 14));
    const remove = Math.min(3650, Math.max(1, Math.round(this.#removeDays) || 90));
    void updateThreadRetention({
        archive_after_days: this.#archiveEnabled ? archive : null,
        remove_after_days: this.#removeEnabled ? remove : null,
    })
        .then((settings) => {
        this.#setArchiveEnabled(settings.archive_after_days != null);
        this.#setArchiveDays(settings.archive_after_days ?? this.#archiveDays);
        this.#setRemoveEnabled(settings.remove_after_days != null);
        this.#setRemoveDays(settings.remove_after_days ?? this.#removeDays);
        this.#setSaved(true);
    })
        .catch((err) => {
        this.#setError(err instanceof Error ? err.message : "Failed to save thread settings.");
    });
}));
    const roots = [scope];
    for (let index = 0; index < roots.length; index++) {
      const candidates = roots[index].nodeType === 1 ? [roots[index], ...roots[index].querySelectorAll('*')] : [...roots[index].querySelectorAll('*')];
      for (const element of candidates) {
        customElements.upgrade(element);
        const hydrate = () => {
          customElements.upgrade(element);
          const definition = customElements.get(element.localName);
          definition?.argonHydrate?.(element);
        };
        if (element.isConnected && customElements.get(element.localName)) hydrate();
        else if (element.localName.includes('-')) customElements.whenDefined(element.localName).then(hydrate);
        else queueMicrotask(hydrate);
        if (element.shadowRoot) roots.push(element.shadowRoot);
      }
    }
  }
}
